import { useState } from "react";
import { motion } from "framer-motion";
import { X, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const inputCls =
  "rounded-xl bg-ink border border-white/10 px-4 py-3 text-sm text-[#F8F6F0] placeholder:text-zinc-600 focus:outline-none focus:border-gold/60 transition-colors";

export const ViewingModal = ({ listing, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", time: "10:00" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/appointments", {
        ...form,
        category: "undecided",
        mode: "zoom",
        message: `Virtual viewing request: ${listing.title} — ${listing.community} (${listing.price_label})`,
      });
      toast.success("Viewing requested — we'll confirm your Zoom walkthrough shortly.");
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send the request. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-ink/80 backdrop-blur-sm" data-testid="viewing-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-md gold-ring rounded-3xl bg-ink-card p-7"
      >
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-serif text-xl text-[#F8F6F0]">Request a virtual viewing</h3>
          <button onClick={onClose} data-testid="viewing-modal-close" className="text-zinc-500 hover:text-zinc-300 p-1" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <p className="text-xs text-zinc-400 mb-5">Live Zoom walkthrough of <span className="text-gold-pale">{listing.title}</span>, wherever you are.</p>
        <form onSubmit={submit} className="space-y-3.5">
          <input required placeholder="Full name" value={form.name} onChange={set("name")} className={`${inputCls} w-full`} data-testid="viewing-name-input" />
          <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={`${inputCls} w-full`} data-testid="viewing-email-input" />
          <input required placeholder="Phone / WhatsApp" value={form.phone} onChange={set("phone")} className={`${inputCls} w-full`} data-testid="viewing-phone-input" />
          <div className="grid grid-cols-2 gap-3">
            <input required type="date" min={new Date().toISOString().split("T")[0]} value={form.date} onChange={set("date")}
                   className={`${inputCls} [color-scheme:dark]`} data-testid="viewing-date-input" />
            <select value={form.time} onChange={set("time")} className={inputCls} data-testid="viewing-time-select">
              {["09:00","10:00","11:00","12:00","14:00","15:00","16:00","17:00","18:00"].map((t) => <option key={t}>{t} GST</option>)}
            </select>
          </div>
          <button type="submit" disabled={busy} data-testid="viewing-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-ink hover:bg-gold-hover disabled:opacity-60 transition-colors">
            {busy ? "Sending…" : "Request viewing"} <Send size={14} />
          </button>
        </form>
      </motion.div>
    </div>
  );
};
